import { Ban } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useAuth } from "@/hooks/useAuth";
import { useBlacklist } from "@/hooks/useBlacklist";

const KST_OFFSET_MS = 9 * 60 * 60 * 1000;

// 등록일을 KST 고정으로 표기 (브라우저 TZ 무관 — SearchResultOrderCard.formatStatusTimestampKst 패턴).
// date-fns format(new Date(...))는 브라우저 로컬이라 여기 쓰면 안 된다.
function formatRegisteredDateKst(value: string): string | null {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const kst = new Date(d.getTime() + KST_OFFSET_MS);
  const y = kst.getUTCFullYear();
  const m = String(kst.getUTCMonth() + 1).padStart(2, "0");
  const day = String(kst.getUTCDate()).padStart(2, "0");
  return `${y}. ${m}. ${day}`;
}

/**
 * 대시보드 거파 명단 카드 — 읽기 전용 (등록/해제는 주문 페이지).
 * 조회 실패는 빈 명단처럼 숨기지 않는다 — 실제 거파 고객이 있어도 "없음"으로 읽히기 때문.
 */
export default function BlacklistSummaryCard() {
  const { user } = useAuth();
  const { blacklistEntries, blacklistStatus } = useBlacklist({ user });

  if (blacklistStatus === "loading") return null;
  if (blacklistStatus === "ready" && blacklistEntries.length === 0) return null;

  const isError = blacklistStatus === "error";

  return (
    <Card className="border-l-4 border-l-red-600">
      <CardHeader>
        <CardTitle className="flex items-center">
          <Ban className="w-4 h-4 mr-2 text-red-500" aria-hidden="true" />
          거파 고객{isError ? "" : ` ${blacklistEntries.length}명`}
        </CardTitle>
        <CardDescription>
          등록된 고객이 고객 목록에 다시 나타나면 배지로 표시됩니다.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isError ? (
          <p className="text-sm text-gray-500 py-2">
            명단을 불러오지 못했습니다.
            <br />
            주문 페이지에서 확인해 주세요.
          </p>
        ) : (
          <>
            {/* 읽기 전용 목록이라 내부에 포커스 가능한 요소가 없다.
                tabIndex로 스크롤 컨테이너 자체를 포커스 대상으로 만들어야 키보드로 아래 항목까지 볼 수 있다. */}
            <ul
              tabIndex={0}
              aria-label="거파 고객 목록"
              className="max-h-[50vh] overflow-y-auto divide-y rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
            >
              {blacklistEntries.map((entry) => {
                const registeredAt = formatRegisteredDateKst(entry.created_at);
                return (
                  <li key={entry.id} className="py-2">
                    <p className="text-sm font-semibold text-gray-800 truncate">{entry.nickname}</p>
                    {entry.reason && (
                      <p className="text-xs text-gray-500 whitespace-pre-wrap break-words">
                        {entry.reason}
                      </p>
                    )}
                    {registeredAt && (
                      <p className="text-[11px] text-gray-500">{registeredAt} 등록</p>
                    )}
                  </li>
                );
              })}
            </ul>
            <p className="mt-3 text-xs text-gray-500">
              등록과 해제는 주문 페이지에서 할 수 있습니다.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
